import { useBrandLocale } from '../../../hooks/useBrandLocale';
import { mpgToLitresPer100Km } from '../../../utils/units';
import React, { useState, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { useMutation as useApolloMutation, useQuery as useApolloQuery } from '@apollo/client';
import { ADD_NEW_CAR_DRAFT, UPDATE_CAR } from '../../../services/carService/queries';
import { useEditingCarDraft } from '../../../hooks/useEditingCarDraft';
import {
  IUpdateCarMutation,
  IUpdateCarMutationVariables,
} from '../../../services/carService/__generated__/queries.generated';
import { carActions } from '../../../../store/carStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCarInfoFromVin } from '../../../helpers/getCarInfoFromVin.async';
import { Modal } from '../../../components/modal';
import { GearType, GasType, GasGrade } from '../../../../__generated__/graphql';
import { authStore } from '../../../../store/authStore';
import { GET_LOCATION_LEAN } from '../../../services/locationServices/queries';
import { formatAddress } from '../../../helpers/formatAddress';

// CategoryEnum will be available after running codegen
type CategoryEnum =
  | 'SUV'
  | 'MINIVAN'
  | 'SEDAN'
  | 'WAGON'
  | 'SUBRUBAN'
  | 'COUPE'
  | 'TRUCK'
  | 'CONVERTIBLE'
  | 'OTHERS';

const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;

const Container = styled.div`
  ${tw`max-w-lg mx-auto p-6 flex flex-col items-center justify-center`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold mb-8`}
`;

const VinSection = styled.div`
  ${tw`mb-6`}
`;

const SectionTitle = styled.h2`
  ${tw`text-xl font-bold mb-2`}
`;

const Description = styled.p`
  ${tw`text-text-secondary mb-4`}
`;

const Label = styled.label`
  ${tw`block text-text-secondary font-medium mb-2`}
`;

const InputContainer = styled.div`
  ${tw`w-full mx-auto mt-6 mb-4`}
`;

const ErrorMessage = styled.p`
  ${tw`text-red-500 text-sm mt-1`}
`;

const StyledInput = styled.input`
  ${tw`w-full p-2 border border-border-default rounded-md`}
  box-sizing: border-box;
`;

const NextButton = styled.button<{ isActivated: boolean }>`
  ${tw`w-full text-white py-3 rounded-lg font-semibold transition-colors shadow-xs`}
  ${({ isActivated }) =>
    isActivated
      ? css`
          ${tw`bg-black`}

          &:hover {
            ${tw`bg-black-hover`}
          }
        `
      : tw`bg-bg-secondary text-text-tertiary cursor-not-allowed`}
`;

const ButtonContainer = styled.div`
  ${tw`flex justify-center w-full md:col-span-2`}
`;

const InfoSection = styled(VinSection)`
  ${tw`mt-6`}
`;

// Add a new styled component for selects
const StyledSelect = styled.select`
  ${tw`w-full p-2 border border-border-default rounded-md`}
  box-sizing: border-box;
  height: 41px; // Match input height
`;

const StyledTextArea = styled.textarea`
  ${tw`w-full p-2 border border-border-default rounded-md`}
  box-sizing: border-box;
`;

const FormGrid = styled.div`
  ${tw`
    grid
    grid-cols-1
    md:grid-cols-2
    gap-4
    w-full
  `}
`;

const FormGroup = styled.div`
  ${tw`mb-4 w-full`}
`;

// Description should span full width
const DescriptionGroup = styled(FormGroup)`
  ${tw`md:col-span-2`}
`;

// Add a required indicator component
const RequiredIndicator = styled.span`
  ${tw`text-red-500 ml-1`}
`;

export default function AddVinPage() {
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [modelYear, setModelYear] = useState('');
  const [seats, setSeats] = useState<number | null>(null);
  const [showCarInfoModal, setShowCarInfoModal] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { editingCar, ready: editingCarReady } = useEditingCarDraft();

  useEffect(() => {
    if (editingCar) {
      setCurrentVIN(editingCar.vin || '');
      setMake(editingCar.make || '');
      setModel(editingCar.model || '');
      setModelYear(editingCar.year?.toString() || '');
      setSeats(editingCar.seats || null);
      setGearType((editingCar.gearType as GearType) || GearType.Automatic);
      setGasType((editingCar.gas as GasType) || GasType.Gasoline);
      setLicensePlateNumber(editingCar.licensePlate || '');
      setLicensePlateState(editingCar.licensePlateState || '');
      setOriginalPrice(editingCar.originalPrice?.toString() || '');
      setGasGrade((editingCar.gasGrade as GasGrade) || GasGrade.Regular);
      setCityMpg(editingCar.cityMpg?.toString() || '');
      setHwyMpg(editingCar.hwyMpg?.toString() || '');
      setDoors(editingCar.doors?.toString() || '');
      setDescription(editingCar.description || '');
    }
  }, [editingCar?.id]);

  // Fallback create if draft missing — lean mutation, no GET_ALL_CARS refetch.
  const [addNewCar] = useApolloMutation(ADD_NEW_CAR_DRAFT);

  const [updateCar] = useApolloMutation<IUpdateCarMutation, IUpdateCarMutationVariables>(
    UPDATE_CAR,
  );

  const locationIdFromUrl = useMemo(() => {
    const locationIdParam = searchParams.get('locationId');
    if (!locationIdParam) return null;
    const id = parseInt(locationIdParam, 10);
    return Number.isFinite(id) ? id : null;
  }, [searchParams]);

  const draftMatchesUrl =
    editingCar?.locationId != null &&
    locationIdFromUrl != null &&
    Number(editingCar.locationId) === locationIdFromUrl;

  const {
    data: locationData,
    loading: locationLoading,
    error: locationError,
  } = useApolloQuery(GET_LOCATION_LEAN, {
    variables: { id: locationIdFromUrl || 0 },
    skip: locationIdFromUrl == null,
    fetchPolicy: draftMatchesUrl ? 'cache-first' : 'network-only',
  });

  const selectedLocation = locationData?.location as
    | {
        id: number;
        userId?: string | null;
        address?: string | null;
        city?: string | null;
        state?: string | null;
        zipCode?: string | null;
        name?: string | null;
      }
    | undefined;

  // Validate locationId from URL without loading the full inventory
  useEffect(() => {
    if (locationIdFromUrl == null) {
      navigate('/cars/host/add');
      return;
    }

    if (draftMatchesUrl) {
      return;
    }

    if (locationLoading) {
      return;
    }

    if (locationError || !selectedLocation) {
      navigate('/cars/host/add');
      return;
    }

    if (selectedLocation.userId !== authStore.user?.id) {
      navigate('/cars/host/add');
    }
  }, [
    locationIdFromUrl,
    draftMatchesUrl,
    locationLoading,
    locationError,
    selectedLocation,
    navigate,
  ]);

  const [currentVIN, setCurrentVIN] = useState('');
  const [gearType, setGearType] = useState<GearType>(GearType.Automatic);
  const [gasType, setGasType] = useState<GasType>(GasType.Gasoline);
  const [licensePlate, setLicensePlateNumber] = useState('');
  const [licensePlateState, setLicensePlateState] = useState('');
  const [originalPrice, setOriginalPrice] = useState<string>('');
  const [gasGrade, setGasGrade] = useState<GasGrade>(GasGrade.Regular);
  const [cityMpg, setCityMpg] = useState<string>('');
  const [hwyMpg, setHwyMpg] = useState<string>('');
  const [doors, setDoors] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [category, setCategory] = useState<CategoryEnum | null>(null);
  const [isLoadingVin, setIsLoadingVin] = useState(false);
  const [isSubmittingCarInfo, setIsSubmittingCarInfo] = useState(false);
  const [vinError, setVinError] = useState('');

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toUpperCase();
    setCurrentVIN(value);
    setVinError('');
  };

  const onSubmitVin = async () => {
    if (!VIN_REGEX.test(currentVIN)) {
      setVinError('Invalid VIN format (must be 17 characters, excluding I, O, Q)');
      return;
    }

    setIsLoadingVin(true);
    try {
      const { Make, Model, ModelYear, Seats } = await getCarInfoFromVin(currentVIN);
      Make && setMake(Make);
      Model && setModel(Model);
      ModelYear && setModelYear(ModelYear);
      Seats && setSeats(Number(Seats));
      setShowCarInfoModal(true);
    } catch (error) {
      setShowCarInfoModal(true);
    } finally {
      setIsLoadingVin(false);
    }
  };
  const onHandleMakeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMake(event.target.value);
  };
  const onHandleModelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setModel(event.target.value);
  };
  const onHandleModelYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setModelYear(event.target.value);
  };
  const onHandleSeatsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSeats(Number(event.target.value));
  };
  const onHandleGearTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setGearType(event.target.value as GearType);
  };
  const onHandleGasTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setGasType(event.target.value as GasType);
  };
  const onHandleLicensePlateNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLicensePlateNumber(event.target.value);
  };
  const onHandleLicensePlateStateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLicensePlateState(event.target.value);
  };
  const onHandleOriginalPriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^0-9.]/g, '');
    setOriginalPrice(value);
  };
  const onHandleGasGradeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setGasGrade(event.target.value as GasGrade);
  };
  const onHandleCityMpgChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^0-9]/g, '');
    setCityMpg(value);
  };
  const onHandleHwyMpgChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^0-9]/g, '');
    setHwyMpg(value);
  };
  const onHandleDoorsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^0-9]/g, '');
    setDoors(value);
  };
  const onHandleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(event.target.value);
  };
  const onHandleCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCategory((event.target.value as CategoryEnum) || null);
  };
  const onSubmitCarInfo = async () => {
    if (!authStore.user?.id) {
      return;
    }

    // Get locationId from URL params
    const locationIdParam = searchParams.get('locationId');
    if (!locationIdParam) {
      navigate('/cars/host/add');
      return;
    }

    const locationId = parseInt(locationIdParam, 10);
    if (isNaN(locationId)) {
      navigate('/cars/host/add');
      return;
    }

    if (!draftMatchesUrl && (!selectedLocation || selectedLocation.userId !== authStore.user.id)) {
      navigate('/cars/host/add');
      return;
    }

    setIsSubmittingCarInfo(true);
    try {
      if (!editingCar?.id) {
        const response = await addNewCar({
          variables: {
            carInputData: {
              vin: currentVIN,
              make,
              model,
              year: Number(modelYear),
              name: `${make} ${model} ${modelYear}`,
              seats: seats,
              gearType,
              gas: gasType,
              isCreated: false,
              licensePlate: licensePlate,
              licensePlateState,
              originalPrice: parseFloat(originalPrice),
              gasGrade: gasGrade,
              cityMpg: cityMpg ? parseInt(cityMpg) : null,
              hwyMpg: hwyMpg ? parseInt(hwyMpg) : null,
              doors: doors ? parseInt(doors) : null,
              description: description || null,
              ...(category && { category: category as any }),
              ownerId: authStore.user.id,
              locationId: locationId,
            },
          },
        });
        const newCar = response.data?.addNewCar;
        newCar?.id &&
          carActions.startEditing(newCar.id, {
            ...newCar,
            __typename: 'Car',
            priceCalendar: [],
            discount: null,
            tripPreferences: null,
            owner: {
              __typename: 'User',
              id: authStore.user.id,
              email: authStore.user.email || '',
              displayName: authStore.user.displayName || '',
            },
            ownerId: authStore.user.id,
            locationId: locationId,
          });
      } else {
        if (
          currentVIN === editingCar.vin &&
          make === editingCar.make &&
          model === editingCar.model &&
          Number(modelYear) === editingCar.year &&
          seats === editingCar.seats &&
          gearType === editingCar.gearType &&
          gasType === editingCar.gas &&
          licensePlate === editingCar.licensePlate &&
          licensePlateState === editingCar.licensePlateState &&
          originalPrice === editingCar.originalPrice?.toString() &&
          gasGrade === editingCar.gasGrade &&
          cityMpg === editingCar.cityMpg?.toString() &&
          hwyMpg === editingCar.hwyMpg?.toString() &&
          doors === editingCar.doors?.toString() &&
          description === editingCar.description &&
          (category || null) === (editingCar.category || null)
        ) {
          navigate('/cars/host/add-price');
          return;
        }
        const response = await updateCar({
          variables: {
            id: editingCar.id,
            updateCarInput: {
              vin: currentVIN,
              make,
              model,
              name: `${make} ${model} ${modelYear}`,
              year: Number(modelYear),
              seats: seats,
              gearType,
              gas: gasType,
              licensePlate,
              licensePlateState,
              originalPrice: parseFloat(originalPrice),
              gasGrade: gasGrade,
              cityMpg: cityMpg ? parseInt(cityMpg) : null,
              hwyMpg: hwyMpg ? parseInt(hwyMpg) : null,
              doors: doors ? parseInt(doors) : null,
              description: description || null,
              ...(category && { category: category as any }),
            },
          },
        });
        const updatedCar = response.data?.updateCar;
        updatedCar?.id && carActions.startEditing(updatedCar.id, updatedCar);
      }
      navigate('/cars/host/add-price');
    } catch (error) {
    } finally {
      setIsSubmittingCarInfo(false);
    }
  };
  if (locationIdFromUrl == null) {
    return <Container>Loading...</Container>;
  }

  if (!draftMatchesUrl && locationLoading) {
    return <Container>Loading...</Container>;
  }

  if (!draftMatchesUrl && (locationError || !selectedLocation)) {
    return (
      <Container>
        <Title>Location not found</Title>
        <ButtonContainer>
          <NextButton isActivated onClick={() => navigate('/cars/host/add')}>
            Choose Location
          </NextButton>
        </ButtonContainer>
      </Container>
    );
  }
  if (!editingCarReady) {
    return <div>Error</div>;
  }

  return (
    <Container>
      <Title>List your car</Title>
      {selectedLocation && (
        <VinSection>
          <SectionTitle>Location</SectionTitle>
          <Description>
            {formatAddress(selectedLocation, {
              includeState: false,
              includeZipCode: false,
            })}
          </Description>
        </VinSection>
      )}
      <VinSection>
        <SectionTitle>VIN Number</SectionTitle>
        <Description>Needs your Vin Number to ensure we add the correct car</Description>
        <Label>Where is your car VIN Number?</Label>
        <InputContainer>
          <StyledInput
            type="text"
            placeholder="Enter Vin"
            value={currentVIN}
            onChange={handleInputChange}
            maxLength={17}
            data-testid="car-vin-input"
          />
          {vinError && <ErrorMessage>{vinError}</ErrorMessage>}
        </InputContainer>
      </VinSection>

      <ButtonContainer>
        <NextButton
          disabled={!currentVIN || isLoadingVin}
          isActivated={!!currentVIN && !isLoadingVin}
          onClick={onSubmitVin}
          data-testid="car-vin-next"
        >
          {isLoadingVin ? 'Loading...' : 'Next'}
        </NextButton>
      </ButtonContainer>
      <Modal isOpen={showCarInfoModal} onClose={() => setShowCarInfoModal(false)} title="My Modal">
        <CarInfo
          make={make}
          model={model}
          modelYear={modelYear}
          seats={seats}
          gearType={gearType}
          gasType={gasType}
          onHandleMakeChange={onHandleMakeChange}
          onHandleModelChange={onHandleModelChange}
          onHandleModelYearChange={onHandleModelYearChange}
          onHandleSeatsChange={onHandleSeatsChange}
          onHandleGearTypeChange={onHandleGearTypeChange}
          onHandleGasTypeChange={onHandleGasTypeChange}
          onHandleLicensePlateNumberChange={onHandleLicensePlateNumberChange}
          onHandleLicensePlateStateChange={onHandleLicensePlateStateChange}
          onSubmitCarInfo={onSubmitCarInfo}
          licensePlate={licensePlate}
          licensePlateState={licensePlateState}
          originalPrice={originalPrice}
          onHandleOriginalPriceChange={onHandleOriginalPriceChange}
          gasGrade={gasGrade}
          cityMpg={cityMpg}
          hwyMpg={hwyMpg}
          doors={doors}
          description={description}
          onHandleGasGradeChange={onHandleGasGradeChange}
          onHandleCityMpgChange={onHandleCityMpgChange}
          onHandleHwyMpgChange={onHandleHwyMpgChange}
          onHandleDoorsChange={onHandleDoorsChange}
          onHandleDescriptionChange={onHandleDescriptionChange}
          category={category}
          onHandleCategoryChange={onHandleCategoryChange}
          isSubmitting={isSubmittingCarInfo}
        />
      </Modal>
    </Container>
  );
}

export const CarInfo = ({
  make,
  model,
  modelYear,
  seats,
  gearType,
  gasType,
  onHandleMakeChange,
  onHandleModelChange,
  onHandleModelYearChange,
  onHandleSeatsChange,
  onHandleGearTypeChange,
  onHandleGasTypeChange,
  onHandleLicensePlateNumberChange,
  onHandleLicensePlateStateChange,
  onSubmitCarInfo,
  licensePlate,
  licensePlateState,
  originalPrice,
  onHandleOriginalPriceChange,
  gasGrade,
  cityMpg,
  hwyMpg,
  doors,
  description,
  onHandleGasGradeChange,
  onHandleCityMpgChange,
  onHandleHwyMpgChange,
  onHandleDoorsChange,
  onHandleDescriptionChange,
  category,
  onHandleCategoryChange,
  isSubmitting = false,
}: {
  make: string;
  model: string;
  modelYear: string;
  seats: number | null;
  gearType: GearType;
  gasType: GasType;
  onHandleMakeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onHandleModelChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onHandleModelYearChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onHandleSeatsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onHandleGearTypeChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onHandleGasTypeChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onHandleLicensePlateNumberChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onHandleLicensePlateStateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmitCarInfo: () => void;
  licensePlate: string;
  licensePlateState: string;
  originalPrice: string;
  onHandleOriginalPriceChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  gasGrade: GasGrade;
  cityMpg: string;
  hwyMpg: string;
  doors: string;
  description: string;
  onHandleGasGradeChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onHandleCityMpgChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onHandleHwyMpgChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onHandleDoorsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onHandleDescriptionChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  category: CategoryEnum | null;
  onHandleCategoryChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  isSubmitting?: boolean;
}) => {
  // Spec 028 D7: MPG is stored; a metric brand also sees litres per 100 km.
  const metric = useBrandLocale().distanceUnit === 'km';
  const litres = (mpg: string) =>
    metric && Number(mpg) > 0 ? ` (${mpgToLitresPer100Km(Number(mpg))} L/100 km)` : '';
  return (
    <InfoSection>
      <FormGrid>
        <FormGroup>
          <Label>
            Make<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledInput
              type="text"
              value={make ?? ''}
              placeholder="Vehicle Make"
              onChange={onHandleMakeChange}
              data-testid="car-make-input"
            />
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            Model<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledInput
              type="text"
              value={model ?? ''}
              placeholder="Vehicle Model"
              onChange={onHandleModelChange}
              data-testid="car-model-input"
            />
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            Model Year<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledInput
              type="text"
              value={modelYear ?? ''}
              placeholder="Vehicle Year"
              onChange={onHandleModelYearChange}
              data-testid="car-year-input"
            />
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            Seats<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledInput
              type="text"
              value={seats?.toString() ?? ''}
              placeholder="Vehicle Seats"
              onChange={onHandleSeatsChange}
              data-testid="car-seats-input"
            />
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            Gear Type<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledSelect
              value={gearType}
              onChange={onHandleGearTypeChange}
              data-testid="car-gear-type-select"
            >
              <option value={GearType.Automatic}>Automatic</option>
              <option value={GearType.Manual}>Manual</option>
              <option value={GearType.SemiAutomatic}>Semi-Automatic</option>
            </StyledSelect>
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            Fuel Type<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledSelect
              value={gasType}
              onChange={onHandleGasTypeChange}
              data-testid="car-fuel-type-select"
            >
              {Object.values(GasType).map((type: GasType) => (
                <option key={type} value={type}>
                  {type.charAt(0) + type.slice(1).toLowerCase()}
                </option>
              ))}
            </StyledSelect>
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            License Plate Number<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledInput
              type="text"
              value={licensePlate}
              placeholder="Enter License Plate Number"
              onChange={onHandleLicensePlateNumberChange}
              data-testid="car-license-plate-input"
            />
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            License Plate State<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledInput
              type="text"
              value={licensePlateState}
              placeholder="Enter State (e.g., CA)"
              onChange={onHandleLicensePlateStateChange}
              data-testid="car-license-state-input"
            />
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            Original Price ($) <span tw="text-text-tertiary text-sm font-normal">(optional)</span>
          </Label>
          <InputContainer>
            <StyledInput
              type="text"
              value={originalPrice}
              placeholder="Enter Original Price"
              pattern="^\d+(\.\d{1,2})?$"
              onChange={onHandleOriginalPriceChange}
              data-testid="car-original-price-input"
            />
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            Gas Grade<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledSelect
              value={gasGrade}
              onChange={onHandleGasGradeChange}
              data-testid="car-gas-grade-select"
            >
              {Object.values(GasGrade).map((grade: GasGrade) => (
                <option key={grade} value={grade}>
                  {grade.charAt(0) + grade.slice(1).toLowerCase()}
                </option>
              ))}
            </StyledSelect>
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            City MPG{litres(cityMpg)}
            <RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledInput
              type="text"
              value={cityMpg}
              placeholder="Enter City MPG"
              onChange={onHandleCityMpgChange}
              data-testid="car-city-mpg-input"
            />
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            Highway MPG{litres(hwyMpg)}
            <RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledInput
              type="text"
              value={hwyMpg}
              placeholder="Enter Highway MPG"
              onChange={onHandleHwyMpgChange}
              data-testid="car-highway-mpg-input"
            />
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            Number of Doors<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledInput
              type="text"
              value={doors}
              placeholder="Enter Number of Doors"
              onChange={onHandleDoorsChange}
              data-testid="car-doors-input"
            />
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            Category<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledSelect
              value={category || ''}
              onChange={onHandleCategoryChange}
              data-testid="car-category-select"
            >
              <option value="">Select Category</option>
              <option value="SUV">SUV</option>
              <option value="MINIVAN">Minivan</option>
              <option value="SEDAN">Sedan</option>
              <option value="WAGON">Wagon</option>
              <option value="SUBRUBAN">Suburban</option>
              <option value="COUPE">Coupe</option>
              <option value="TRUCK">Truck</option>
              <option value="CONVERTIBLE">Convertible</option>
              <option value="OTHERS">Others</option>
            </StyledSelect>
          </InputContainer>
        </FormGroup>

        <DescriptionGroup>
          <Label>
            Description<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledTextArea
              value={description}
              placeholder="Enter Vehicle Description"
              onChange={onHandleDescriptionChange}
              data-testid="car-description-input"
            />
          </InputContainer>
        </DescriptionGroup>

        <ButtonContainer>
          <NextButton
            onClick={onSubmitCarInfo}
            disabled={isSubmitting}
            isActivated={
              !isSubmitting &&
              !!make &&
              !!model &&
              !!modelYear &&
              !!seats &&
              !!gearType &&
              !!gasType &&
              !!licensePlate &&
              !!licensePlateState &&
              !!gasGrade &&
              !!cityMpg &&
              !!hwyMpg &&
              !!doors &&
              !!description &&
              !!category
            }
            data-testid="car-info-next"
          >
            {isSubmitting ? 'Saving...' : 'Next'}
          </NextButton>
        </ButtonContainer>
      </FormGrid>
    </InfoSection>
  );
};
